import { yup } from '@framework/validation/initYup.ts';
import { LIVE_FIELD_PATHS_META_KEY } from '@framework/validation/live/live.ts';
import { isStaticActive } from '../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { getPagesInRoot } from '../../layout/pages/getPagesInRoot.ts';
import { type IEngineSection } from '../../schema/sections/IEngineSection.ts';
import {
  type IParentWidgetScope,
  type IParentWidgetScopeItem,
} from '../../schema/widgets/IWidgetType.ts';
import { WidgetRepository } from '../../schema/widgets/repository/WidgetRepository.ts';
import { type Widget } from '../../schema/widgets/Widget.ts';
import { clearSanitizeHtmlCache } from '../../ui/html/cachedSanitizeHtml.ts';
import { clearStripHtmlForDisplayCache } from '../../ui/html/stripHtmlForDisplay.tsx';
import { setupEngineLiveContext } from '../engineLiveContextTransform.ts';
import { createSectionsStatusSchema } from '../status/SectionStatusSchema.ts';
import { clearStripHtmlCache } from '../utils/cachedStripHtml.ts';
import { mergeList } from '../utils/mergeList.ts';
import {
  isCiclicDependencyError,
  manageCiclicDependencyError,
} from './manageCiclicDependacyError.ts';
import { setStepValidation } from './setValidationStep.ts';
import { setWidgetTypeMeta } from './setWidgetTypeMeta.ts';
export class FormValidatorBuilder {
  /**
   * Fornita una lista di sezioni, genera il validatore per il
   * corrispetivo form.
   */
  static build(sections: IEngineSection[]) {
    clearStripHtmlCache();
    clearStripHtmlForDisplayCache();
    clearSanitizeHtmlCache();
    try {
      return {
        schema: yup
          .object()
          .shape(
            mergeList(FormValidatorBuilder.createSectionsSchemaShape(sections)),
          )
          .required()
          .noUnknown(),
      };
    } catch (e: any) {
      if (isCiclicDependencyError(e)) {
        return manageCiclicDependencyError(e);
      }

      throw e;
    }
  }

  static buildSection(section: IEngineSection) {
    return FormValidatorBuilder.buildSections([section]);
  }

  /**
   * Fornita una lista di sezioni, genera il validatore corrispondente.
   *
   * @param rootScope Oggetto con le proprietà da aggiungere allo scope in tutti i casi, ad esempio per gestire il readonly
   */
  static buildSections(
    sections: IEngineSection[],
    rootScope: Partial<IParentWidgetScopeItem> = {},
  ) {
    try {
      const shape = mergeList([
        createSectionsStatusSchema(sections),
        ...sections.map(section => ({
          [section.id]: yup
            .object(
              mergeList(
                FormValidatorBuilder.createSectionsSchemaShape(
                  [section],
                  rootScope,
                ),
              ),
            )
            .meta({ sectionId: section.id })
            .addChildrenParentDeps(),
        })),
      ]);

      const { contextTransform, valuesTransform, liveFieldPaths } =
        setupEngineLiveContext(sections);
      const schema = yup
        .object()
        .shape(shape)
        .required()
        .noUnknown()
        .setLiveContextTransform(contextTransform)
        .setLiveValuesTransform(valuesTransform)
        .meta({ [LIVE_FIELD_PATHS_META_KEY]: liveFieldPaths });

      return {
        schema,
      };
    } catch (e: any) {
      if (isCiclicDependencyError(e)) {
        return manageCiclicDependencyError(e);
      }

      throw e;
    }
  }

  /**
   * Restituisce una shape (oggetto { chiave: schema } da utilizzare in `yup.object().shape()`)
   * di una specifica sezione.
   */
  static createSectionsSchemaShape(
    sections: IEngineSection[],
    rootScope: Partial<IParentWidgetScopeItem> = {},
  ) {
    // Costruisco la struttura "a pagine" delle sezioni così da poter sapere
    // in quale step ogni widget è contenuto. (utile per la validazione parziale)
    const sectionPages = sections.map((section, index) => ({
      section,
      pages: getPagesInRoot(section.ultimaVersione?.elementi, index),
    }));

    // Costruisco la shape passando lo step di ogni pagina di widget (in maniera incrementale
    // come nel navigator)
    const schemaShape = sectionPages.flatMap(({ section, pages }) =>
      pages.map(page => {
        const result = FormValidatorBuilder.createWidgetsSchemaShape(
          page.widgets,
          page.sectionIndex,
          page.pageIndex,
          FormValidatorBuilder.buildSectionParentScope(section, rootScope),
        );
        delete result.__isWidgetSchemaShape;
        return result;
      }),
    );

    return schemaShape;
  }

  /**
   * Costruisce lo scope parent per la sezione fornita, in modo che la
   * validazione possa essere disabilitata o resa non visibile.
   *
   * @param partialRootScope Oggetto con le proprietà da aggiungere allo scope in tutti i casi
   */
  private static buildSectionParentScope(
    section: IEngineSection,
    partialRootScope: Partial<IParentWidgetScopeItem> = {},
  ) {
    if (
      isStaticActive(section.ultimaVersione?.visibile) &&
      isStaticActive(section.ultimaVersione?.compilabile)
    ) {
      return [
        {
          visible: undefined,
          enabled: undefined,
          code: section.id,
          ...section.sectionRootScope,
          // Lo scope passato a livello di creazione del validatore è quello
          // più forte, quindi sovrascrive le proprietà della sezione
          ...partialRootScope,
        },
      ];
    }

    return [
      {
        visible: section.ultimaVersione?.visibile,
        enabled: section.ultimaVersione?.compilabile,
        code: section.id,
        ...section.sectionRootScope,
        ...partialRootScope,
      },
    ];
  }

  /**
   * Data una lista di widget (e.g. `elementi` di una sezione o `children` di un container)
   * restituisce una shape (oggetto { chiave: schema } da utilizzare in `yup.object().shape()`)
   * del validatore corrispondente.
   */
  static createWidgetsSchemaShape(
    widgets: Widget[],
    section: number,
    page: number,
    parentScope: IParentWidgetScope = [],
  ) {
    if (!widgets) return {};

    const result = mergeList(
      widgets?.map((widget: Widget) => {
        return FormValidatorBuilder.createWidgetSchemaShape(
          widget,
          section,
          page,
          parentScope,
        );
      }) ?? [],
    );

    return { ...result, __isWidgetSchemaShape: true };
  }

  /**
   * Restituisce una shape (oggetto { chiave: schema } da utilizzare in `yup.object().shape()`)
   * del validatore del widget fornito.
   */
  static createWidgetSchemaShape(
    widget: Widget,
    section: number,
    page: number,
    parentScope: IParentWidgetScope,
  ) {
    const widgetType = WidgetRepository.find(widget.type);
    if (!widgetType?.createFormSchema) {
      return {};
    }
    const widgetSchema = widgetType.createFormSchema({
      widget,
      parentScope,
      section,
      page,
    });

    if (widgetSchema.__isWidgetSchemaShape) {
      delete widgetSchema.__isWidgetSchemaShape;
      return widgetSchema;
    }

    // Accumulo i meta extra forniti da tutti i parent per impostarli nel widget
    const parentScopeMeta = parentScope.reduce(
      (acc, scope) => Object.assign(acc, scope.extraMeta),
      {} as Record<string, any>,
    );

    // Imposta la validazione per evitare di validare step (sezioni/pagine) ancora non visualizzate
    // Utilizzo Object.keys perché lo schema ritorna nella forma { [widget-code]: schema }, ma in realtà gira 1 sola volta
    for (const key of Object.keys(widgetSchema)) {
      widgetSchema[key] = widgetSchema[key].withMutation(
        (schema: yup.AnySchema) => {
          setStepValidation(schema, widget, widgetType, {
            widgetSectionStep: section,
            widgetPageStep: page,
            parentScope,
          });

          setWidgetTypeMeta(schema, widget);

          schema.meta(parentScopeMeta);
          return schema;
        },
      );
    }

    return widgetSchema;
  }
}
